import { useAxios } from '@/context/axiosContext'
import '../feedback.css'
import { useLocation, useNavigate } from 'react-router-dom'
import { useDialog } from '@/context/dialogContext'
import { useEffect, useRef, useState } from 'react'
import { Praise } from './praise'
import { Suggestion } from './suggestion'
import { Button, Input, Select } from '@/components'
import {
  FeedbackListData,
  GetFeedbackData,
  KeywordType,
  ListInfo,
  SearchType,
} from '../feedbackItem'
import { useResponsive } from '@/hooks/useResponsive'

type TInput = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement

export const FeedbackList = () => {
  const axios = useAxios()
  const location = useLocation()
  const navigate = useNavigate()
  const { showAlert } = useDialog()
  const { isMobile, isTablet, isDesktop } = useResponsive()
  // 스크롤 위치 참조를 위한 ref 추가
  const containerRef = useRef<HTMLDivElement>(null)

  // 검색 관련 상태 - 실제 검색에 적용될 값들
  const [appliedSearchParams, setAppliedSearchParams] = useState<{
    keyword: string
    keywordTypes: string[]
    rndDt?: string
  }>({
    keyword: '',
    keywordTypes: ['nttSj'],
    rndDt: '',
  })

  // UI 상태 - 사용자가 입력 중인 값들
  const [uiState, setUiState] = useState<{
    keyword: string
    keywordTypes: string[]
    rndDt?: string
  }>({
    keyword: '',
    keywordTypes: ['nttSj'],
    rndDt: '',
  })

  // 정렬 타입 (즉시 적용)
  const [sortType, setSortType] = useState('recent')

  // 각각의 페이지 상태
  const [praisePage, setPraisePage] = useState(1)
  const [suggestionPage, setSuggestionPage] = useState(1)
  // 데이터 상태
  const [praiseList, setPraiseList] = useState<FeedbackListData[]>([])
  const [suggestionList, setSuggestionList] = useState<FeedbackListData[]>([])
  const [praiseCnt, setPraiseCnt] = useState(0)
  const [suggestionCnt, setSuggestionCnt] = useState(0)

  // 컴포넌트 상단에 상태 추가
  const [activeTab, setActiveTab] = useState('good')
  // 탭 클릭 핸들러
  const handleTabClick = (tabType: string) => {
    setActiveTab(tabType)
  }
  // 스크롤업 함수
  const scrollToTop = () => {
    // feedback-container로 스크롤하거나, 페이지 최상단으로 스크롤
    if (containerRef.current) {
      containerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }

  // 정렬 타입이 변경되거나 실제 검색 조건이 변경되면 양쪽 데이터 모두 새로 불러오기
  useEffect(() => {
    if (hasSearchConditions()) {
      loadBothLists()
    }
  }, [sortType, appliedSearchParams])

  // 개별 페이지 변경 시에만 해당 리스트만 업데이트
  // useEffect(() => {
  //   if (hasSearchConditions()) {
  //     getPraiseList()
  //     // 페이지 변경 시 스크롤업
  //     scrollToTop()
  //   }
  // }, [praisePage])

  // useEffect(() => {
  //   if (hasSearchConditions()) {
  //     getSuggestionList()
  //     // 페이지 변경 시 스크롤업
  //     scrollToTop()
  //   }
  // }, [suggestionPage])
  const isFirstRender = useRef(true)

  useEffect(() => {
    if (hasSearchConditions()) {
      getPraiseList()
      if (!isFirstRender.current && !isMobile) scrollToTop()
    }
  }, [praisePage])

  useEffect(() => {
    if (hasSearchConditions()) {
      getSuggestionList()
      if (!isFirstRender.current && !isMobile) scrollToTop()
    }
  }, [suggestionPage])

  // 첫 렌더링 후에는 flag 변경
  useEffect(() => {
    isFirstRender.current = false
  }, [])

  const hasSearchConditions = () => {
    return (
      appliedSearchParams.keywordTypes.length > 0 ||
      appliedSearchParams.keyword.trim() !== ''
    )
  }

  const buildSearchParams = (page: number) => {
    const params: any = { page }

    if (sortType) params.sortType = sortType
    if (appliedSearchParams.keywordTypes.length > 0) {
      params.keywordTypes = appliedSearchParams.keywordTypes.join(',')
    }
    if (appliedSearchParams.keyword.trim())
      params.keyword = appliedSearchParams.keyword
    if (
      appliedSearchParams.keywordTypes.includes('rndDt') &&
      appliedSearchParams.rndDt
    ) {
      params.rndDt = appliedSearchParams.rndDt.replaceAll('-', '')
    }

    return params
  }

  // 검색 조건 적용 시 양쪽 모두 첫 페이지로 리셋하여 로드
  const loadBothLists = async () => {
    setPraisePage(1)
    setSuggestionPage(1)
    await Promise.all([getPraiseList(1), getSuggestionList(1)])
  }

  // 칭찬 리스트만 로드
  const getPraiseList = async (page = praisePage) => {
    try {
      const params = buildSearchParams(page)
      const response = await axios.get('/feedback/getPraiseList', {
        headers: { 'Cache-Control': 'no-cache' },
        params,
      })

      const { praiseList, praiseCount } = response.data.data
      setPraiseList(praiseList || [])
      setPraiseCnt(praiseCount || 0)
    } catch (err) {
      console.error('Error fetching praise list:', err)
      setPraiseList([])
      setPraiseCnt(0)
    }
  }

  // 제안 리스트만 로드
  const getSuggestionList = async (page = suggestionPage) => {
    try {
      const params = buildSearchParams(page)
      const response = await axios.get('/feedback/getSuggestionList', {
        headers: { 'Cache-Control': 'no-cache' },
        params,
      })

      const { suggestionList, suggestionCount } = response.data.data
      setSuggestionList(suggestionList || [])
      setSuggestionCnt(suggestionCount || 0)
    } catch (err) {
      console.error('Error fetching suggestion list:', err)
      setSuggestionList([])
      setSuggestionCnt(0)
    }
  }

  const handleDetailView = (nttSn: number) => {
    navigate(`/feedback/detail?nttSn=${nttSn}`)
  }

  // 정렬 타입 변경 (즉시 적용)
  const handleSortTypeChange = (value: string) => {
    setSortType(value)
  }

  // UI 상태 변경 (검색 버튼 클릭 전까지는 실제 검색에 적용되지 않음)
  const handleKeywordTypesChange = (value: string) => {
    setUiState((prev) => {
      const isChecked = prev.keywordTypes.includes(value)
      return {
        ...prev,
        keywordTypes: isChecked
          ? prev.keywordTypes.filter((v) => v !== value)
          : [...prev.keywordTypes, value],
      }
    })
  }

  // 검색 버튼 클릭 시 UI 상태를 실제 검색 파라미터에 적용
  const handleSearchClick = () => {
    if (uiState.keywordTypes.length === 0) {
      showAlert({
        title: '검색 조건을 하나 이상 선택해주세요.',
      })
      return
    }

    // UI 상태를 실제 검색 파라미터에 적용
    setAppliedSearchParams({
      keyword: uiState.keyword,
      keywordTypes: uiState.keywordTypes,
      rndDt: uiState.rndDt,
    })
  }

  const handleInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearchClick()
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUiState((prev) => ({
      ...prev,
      keyword: e.target.value,
    }))
  }

  const handleDateChange = (e: React.ChangeEvent<TInput>) => {
    const { value } = e.target

    if (new Date(value) > new Date()) {
      showAlert({
        type: 'error',
        title: '오늘 이전 날짜만 선택할 수 있어요.',
      })
      return
    }

    setUiState((prev) => ({
      ...prev,
      rndDt: value,
    }))
  }

  // 현재 페이지에서 실제 표시되는 아이템 수 계산
  const getDisplayedItemCount = (
    totalCount: number,
    currentPage: number,
    itemsPerPage = 10,
  ) => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const remainingItems = totalCount - startIndex
    return Math.min(remainingItems, itemsPerPage)
  }

  // 두 리스트 중 더 많은 아이템을 표시하는 쪽의 아이템 수 계산
  const getMaxDisplayedItems = () => {
    const praiseDisplayed = getDisplayedItemCount(praiseCnt, praisePage)
    const suggestionDisplayed = getDisplayedItemCount(
      suggestionCnt,
      suggestionPage,
    )
    return Math.max(praiseDisplayed, suggestionDisplayed)
  }

  // Praise와 Suggestion 컴포넌트에 전달할 props에 추가
  const maxItems = getMaxDisplayedItems()

  return (
    <>
      {!isMobile ? (
        <>
          <ListInfo />
          <div className="search-container">
            <div className="feedback-search-filter">
              <div className="search-wrapper">
                <label>정렬&nbsp;&nbsp;:&nbsp;&nbsp;</label>
                <div className="radio-group">
                  {SearchType.map((type) => (
                    <label key={type.value}>
                      <input
                        type="radio"
                        name="sortType"
                        value={type.value}
                        checked={sortType === type.value}
                        onChange={() => handleSortTypeChange(type.value)}
                      />
                      {type.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="list-group search-row">
              <div className="feedback-search-filter">
                <div className="search-wrapper">
                  <label>검색&nbsp;&nbsp;:&nbsp;&nbsp;</label>
                  <div className="radio-group">
                    {KeywordType.map((type) => (
                      <label key={type.value}>
                        <input
                          type="checkbox"
                          name="keywordType"
                          value={type.value}
                          checked={uiState.keywordTypes.includes(type.value)}
                          onChange={() => handleKeywordTypesChange(type.value)}
                        />
                        {type.label}
                        {type.value === 'rndDt' &&
                          uiState.keywordTypes.includes('rndDt') && (
                            <input
                              type="date"
                              id="rndDt"
                              name="rndDt"
                              value={uiState.rndDt}
                              onChange={handleDateChange}
                            />
                          )}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="search-box">
                <input
                  type="text"
                  placeholder="검색어 입력"
                  style={{ width: '300px' }}
                  value={uiState.keyword}
                  onChange={handleInputChange}
                  onKeyDown={handleInputKeyPress}
                />
                <Button color="neverbe" onClick={handleSearchClick}>
                  검색
                </Button>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="mobile-fb-container">
          <ListInfo isMobile={isMobile} />
          <section className="search-section">
            <form className="search-form">
              <div className="form-group">
                <label className="form-label">정렬&nbsp;&nbsp;</label>
                <div className="form-options">
                  {SearchType.map((type) => (
                    <label className="radio-option" key={type.value}>
                      <input
                        type="radio"
                        name="sortType"
                        value={type.value}
                        checked={sortType === type.value}
                        onChange={() => handleSortTypeChange(type.value)}
                      />
                      <span>{type.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">검색&nbsp;&nbsp;</label>
                <div className="form-options">
                  {KeywordType.map((type) => (
                    <label className="radio-option" key={type.value}>
                      <input
                        type="checkbox"
                        name="keywordType"
                        value={type.value}
                        checked={uiState.keywordTypes.includes(type.value)}
                        onChange={() => handleKeywordTypesChange(type.value)}
                      />
                      {type.label}
                      {type.value === 'rndDt' &&
                        uiState.keywordTypes.includes('rndDt') && (
                          <input
                            type="date"
                            id="rndDt"
                            name="rndDt"
                            value={uiState.rndDt}
                            onChange={handleDateChange}
                          />
                        )}
                    </label>
                  ))}
                </div>
              </div>

              <div
                className="promo-search-input-group"
                style={{
                  marginTop: '10px',
                }}
              >
                <input
                  type="text"
                  className="promo-search-input"
                  placeholder="검색어 입력"
                  value={uiState.keyword}
                  onChange={handleInputChange}
                  onKeyDown={handleInputKeyPress}
                />
                <button
                  type="button"
                  className="search-btn"
                  onClick={handleSearchClick}
                >
                  검색
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
      {!isMobile ? (
        <div className="spacer" />
      ) : (
        <div className="mobile-fb-container">
          <div className="mobile-fb-mobile-tabs">
            <button
              className={`mobile-fb-tab-btn praise ${activeTab === 'good' ? 'active' : ''}`}
              data-tab="good"
              onClick={() => handleTabClick('good')}
            >
              꿀소리
            </button>
            <button
              className={`mobile-fb-tab-btn suggestion ${activeTab === 'bad' ? 'active' : ''}`}
              data-tab="bad"
              onClick={() => handleTabClick('bad')}
            >
              쓴소리
            </button>
          </div>
        </div>
      )}
      <div
        className={!isMobile ? 'feedback-container' : 'mobile-fb-container'}
        ref={containerRef}
      >
        {(!isMobile || activeTab === 'good') && (
          <Praise
            page={praisePage}
            setPage={setPraisePage}
            praiseList={praiseList}
            praiseCnt={praiseCnt}
            handleDetailView={handleDetailView}
            maxDisplayedItems={maxItems} // 추가
            isMobile={isMobile}
            activeTab={activeTab}
          />
        )}
        {!isMobile && <div className="divider" />}
        {(!isMobile || activeTab === 'bad') && (
          <Suggestion
            page={suggestionPage}
            setPage={setSuggestionPage}
            suggestionList={suggestionList}
            suggestionCnt={suggestionCnt}
            handleDetailView={handleDetailView}
            maxDisplayedItems={maxItems} // 추가
            isMobile={isMobile}
            activeTab={activeTab}
          />
        )}
      </div>
    </>
  )
}
